"use client";

import { SLCopyToClipboardButton } from "@/components/buttons/SLCopyToClipboardButton";
import { formatPhoneNumber } from "@/helpers/formatPhone";
import { useMessages } from "@/lib/i18n/useMessages";
import {
  Box,
  Button,
  ButtonGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

function getAdjustedTitleVariant(
  title: string,
  titleVariant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
): "h1" | "h2" | "h3" | "h4" | "h5" | "h6" {
  if (title.length <= 100) return titleVariant;

  const variantMap: Record<string, "h3" | "h4"> = {
    h1: "h4",
    h2: "h3",
  };
  return variantMap[titleVariant] || titleVariant;
}

export function SLSubpageHeaderTextBlock({
  title,
  titleVariant = "h1",
  subtitle,
  contactInfo,
  backgroundColor,
  textColor,
}: {
  title: string;
  titleVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  subtitle?: string | React.ReactNode;
  contactInfo?: {
    email?: string;
    phone?: string;
  };
  backgroundColor?: string;
  textColor?: string;
}) {
  const theme = useTheme();
  const { home, dialogs } = useMessages();

  const formattedPhone = contactInfo?.phone
    ? formatPhoneNumber(contactInfo.phone)
    : undefined;

  const adjustedTitleVariant = getAdjustedTitleVariant(title, titleVariant);

  return (
    <Stack
      spacing={2}
      gap={2}
      alignItems="flex-start"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: { xs: "300px", md: "450px" },
        background: backgroundColor || theme.palette.secondary.light,
        borderRadius: {
          xs: "0 0 0 0",
          sm: "0 0 0 0",
          md: "20px 0 0 20px",
        },
        justifyContent: "space-between",

        px: { xs: 2, sm: 2, md: 4, lg: 10 },
        py: 6,
      }}
    >
      <Box
        sx={{
          px: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Stack gap={4} height="100%" justifyContent="space-between">
          <Typography
            component="h1"
            variant={adjustedTitleVariant}
            sx={{
              fontSize: theme.typography[adjustedTitleVariant].fontSize,
            }}
            color={textColor}
          >
            <SLHtmlFromLangFile htmlString={title} />
          </Typography>
          {subtitle &&
            (typeof subtitle === "string" ? (
              <Typography
                variant="h5"
                color={textColor || "text.secondary"}
                component="p"
              >
                <SLHtmlFromLangFile htmlString={subtitle} />
              </Typography>
            ) : (
              subtitle
            ))}
        </Stack>

        {contactInfo && (contactInfo.email || contactInfo.phone) && (
          <Stack
            width="100%"
            flexWrap="wrap"
            height="fit-content"
            direction={{
              xs: "column",
              sm: "row",
              md: "row",
              lg: "row",
            }}
            gap={2}
            alignItems={{
              xs: "flex-end",
              sm: "flex-end",
              md: "flex-end",
              lg: "space-between",
            }}
            justifyContent="flex-start"
            sx={{
              pt: 4,
              px: { xs: 1, md: 0 },
            }}
          >
            {contactInfo.email && (
              <ButtonGroup size="small" color="success">
                <Button
                  sx={{
                    padding: 2,
                  }}
                  href={`mailto:${contactInfo.email}`}
                >
                  {contactInfo.email}
                </Button>
                <SLCopyToClipboardButton
                  text={contactInfo.email}
                  iconOnly={true}
                  isButtonGroup={true}
                  size="medium"
                  tooltipText={home.contact.copyButton}
                  successMessage={home.contact.copySuccess}
                  sx={{
                    padding: 2,
                  }}
                />
              </ButtonGroup>
            )}
            {contactInfo.phone && formattedPhone && (
              <ButtonGroup size="small" color="success">
                <Button
                  sx={{
                    padding: 2,
                  }}
                  href={`tel:${contactInfo.phone}`}
                >
                  {formattedPhone}
                </Button>
                <SLCopyToClipboardButton
                  text={contactInfo.phone}
                  iconOnly={true}
                  isButtonGroup={true}
                  size="medium"
                  tooltipText={dialogs.call.copy}
                  successMessage={dialogs.call.copySuccess}
                  sx={{
                    padding: 2,
                  }}
                />
              </ButtonGroup>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
